import { useEffect, useRef } from "react";
import { type Icon, ArrowRight2 } from "iconsax-react";
import cn from "classnames";
import { CloseIcon } from "@/shared/assets/icons";
import { useSlideAnimation, EASING } from "@/shared/hooks";
import { ScrollArea } from "../ScrollArea";
import Button from "../Button";

type IconComponent = Icon | React.ComponentType<{ size?: number | string; color?: string; className?: string }>;

interface Props {
  icon: IconComponent | null;
  iconBgClassName?: string;
  secondIcon: IconComponent | null;
  secondIconBgClassName?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  secondaryTitle?: string;
  secondaryTitleExtra?: React.ReactNode;
  secondaryContent?: React.ReactNode;
  footer?: React.ReactNode;
  secondaryFooter?: React.ReactNode;
  onClose: () => void;
  onBack?: () => void;
  onSecondaryClose?: () => void;
  hasBackground?: boolean;
  zIndex?: number;
  hidden?: boolean;
}

export default function ModalFormSplit({
  icon,
  iconBgClassName,
  secondIcon,
  secondIconBgClassName,
  title,
  subtitle,
  children,
  secondaryTitle,
  secondaryTitleExtra,
  secondaryContent,
  footer,
  secondaryFooter,
  onClose,
  onBack,
  onSecondaryClose,
  hasBackground = true,
  zIndex = 10000,
  hidden = false,
}: Props) {
  const IconComponent = icon as IconComponent | null;
  const SecondIconComponent = secondIcon as IconComponent | null;
  const isExpanded = !!secondaryContent;
  const { phase, canAnimate, handleClose, reopen } = useSlideAnimation(onClose);

  const prevHiddenRef = useRef(hidden);
  useEffect(
    function resetPhaseOnReopen() {
      if (prevHiddenRef.current === true && hidden === false) {
        reopen();
      }
      prevHiddenRef.current = hidden;
    },
    [hidden, reopen],
  );

  useEffect(
    function handleEscapeKey() {
      function handleKeyDown(e: KeyboardEvent) {
        if (e.key !== "Escape") return;
        if (hidden) return;
        const hasHigherOverlay = document.querySelector(".z-\\[10200\\]");
        if (hasHigherOverlay) return;
        handleClose();
      }
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    },
    [handleClose, hidden],
  );

  const isOpen = phase === "open";
  const isInert = hidden || !isOpen;

  return (
    <div
      className={cn(
        "fixed top-0 left-0 w-screen h-screen p-5 flex justify-end cursor-pointer",
        hasBackground && "surface-backdrop blur-level-1",
      )}
      style={{
        zIndex,
        opacity: isOpen && !hidden ? 1 : 0,
        transition: canAnimate ? `opacity 300ms ${EASING}` : "none",
        pointerEvents: isOpen && !hidden ? undefined : "none",
      }}
      aria-hidden={isInert}
      inert={isInert}
      onClick={handleClose}>
      <div
        className={cn(
          "h-full border surface-component-stroke dark:border-grey-100 surface-base-fill radius-2xl p-2 flex overflow-hidden cursor-default",
          isExpanded ? "w-6xl" : "w-xl",
        )}
        style={{
          translate: isOpen && !hidden ? "none" : "100% 0",
          transition: canAnimate ? `translate 300ms ${EASING}, width 300ms ${EASING}` : "none",
        }}
        onClick={(e) => e.stopPropagation()}>
        <div className={cn("flex flex-col gap-5 h-full", isExpanded ? "w-1/2" : "w-full")}>
          <div className="flex flex-col px-5 pt-5 gap-5">
            <div className="flex justify-between items-center">
              {icon && IconComponent && (
                <div
                  className={cn(
                    "w-10 aspect-square radial-glow flex justify-center items-center radius-xs text-white",
                    iconBgClassName,
                  )}>
                  <IconComponent size={20} color="currentColor" />
                </div>
              )}
              <Button
                className="w-10! p-0! aspect-square flex justify-center items-center cursor-pointer content-base-secondary"
                variant="secondary"
                onClick={onBack && isExpanded ? onBack : handleClose}>
                {isExpanded ? <ArrowRight2 size={20} color="currentColor" /> : <CloseIcon />}
              </Button>
            </div>

            <div className="pb-5 border-b surface-base-stroke">
              <h2 className="text-display-2xs content-base-primary">{title}</h2>
              {subtitle && <p className="text-body-regular-md content-base-secondary mt-2">{subtitle}</p>}
            </div>
          </div>

          <ScrollArea className="flex-1 min-h-0">
            <div className="px-5 flex flex-col gap-5">
              {children}
            </div>
          </ScrollArea>

          {footer && <div className="px-5 pb-5 mt-auto shrink-0">{footer}</div>}
        </div>

        {isExpanded && (
          <div className="w-1/2 flex flex-col h-full surface-component-fill radius-xl overflow-hidden">
            <div className="flex flex-col px-5 pt-5">
              {(secondaryTitle || onSecondaryClose) && (
                <div className="flex justify-between items-center pb-5">
                  {secondIcon && SecondIconComponent && (
                    <div
                      className={cn(
                        "w-10 aspect-square radial-glow flex justify-center items-center radius-xs text-white",
                        secondIconBgClassName,
                      )}>
                      <SecondIconComponent size={20} color="currentColor" />
                    </div>
                  )}
                  {onSecondaryClose && (
                    <Button
                      className="w-10! p-0! aspect-square flex justify-center items-center cursor-pointer content-base-secondary ml-auto"
                      variant="secondary"
                      onClick={onSecondaryClose}>
                      <CloseIcon />
                    </Button>
                  )}
                </div>
              )}
              {secondaryTitle && (
                <div className="pb-5 border-b surface-component-stroke flex justify-between items-center">
                  <h2 className="text-display-2xs content-base-primary">{secondaryTitle}</h2>
                  {secondaryTitleExtra}
                </div>
              )}
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="px-5">
                {secondaryContent}
              </div>
            </ScrollArea>

            {secondaryFooter && <div className="px-5 pb-5 mt-auto shrink-0">{secondaryFooter}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
